import { generateBetItemId } from '../utils/ticketHelpers';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BetItem } from './BettingSlipDrawer';
import { getTopEvents, getEvent } from '../api/api';
import { EventSnapshot, Market, Outcome } from '../api/types';
import { OddsButton } from './OddsButton';
import { 
  find1x2Market, 
  findRestzeitGeneric, 
  findOUGeneric, 
  findNextGoal, 
  findHandicap, 
  findBTTSGeneric, 
  findDCGeneric, 
  findH2H,
  find1stHalfMarket,
  getMarketAttr,
  formatTippLabelWithAttr,
  isFilterAvailable,
  getMarketOptionsForSport,
  getLayoutGroupForMarketId
} from '../utils/marketParsers';
import { useTranslation } from '../i18n';
import { translateMarketFilterLabel, formatOddsButtonLabel } from '../utils/marketTranslations';

import { SPORT_EMOJI } from './widgets/DesktopLeftSidebar';

const LockIcon = () => (
  <svg className="w-4 h-4 text-gray-400 block" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
  </svg>
);

const JerseyIcon = ({ color, stripeColor }: { color: string; stripeColor?: string }) => (
  <svg className="w-5 h-4 flex-shrink-0" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 3 L8 1 L16 1 L20 3 L23 6 L20 9 L18 8 L18 19 A1 1 0 0 1 17 20 L7 20 A1 1 0 0 1 6 19 L6 8 L4 9 L1 6 Z" fill={color} stroke="#cbd5e1" strokeWidth="1" />
    <path d="M10 1 L12 4 L14 1" stroke="#cbd5e1" strokeWidth="1" />
    {stripeColor && (
      <>
        <line x1="10" y1="5" x2="10" y2="18" stroke={stripeColor} strokeWidth="1.5" />
        <line x1="12" y1="5" x2="12" y2="18" stroke={stripeColor} strokeWidth="1.5" />
        <line x1="14" y1="5" x2="14" y2="18" stroke={stripeColor} strokeWidth="1.5" />
      </>
    )}
  </svg>
);

function getJerseyColors(teamName: string, isHome: boolean): { color: string; stripeColor?: string } {
  const name = teamName.toLowerCase();
  
  if (name.includes('essen') || name.includes('bayern') || name.includes('köln') || name.includes('arsenal') || name.includes('liverpool') || name.includes('manchester utd')) {
    return { color: '#e11d48' }; // red
  }
  if (name.includes('greuther') || name.includes('fürth') || name.includes('real madrid') || name.includes('chelsea') || name.includes('leipzig')) {
    if (isHome) return { color: '#f8fafc', stripeColor: '#64748b' }; // white with gray stripes
  }
  
  let hash = 0;
  for (let i = 0; i < teamName.length; i++) {
    hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const colors = [
    { color: '#e11d48' }, // Red
    { color: '#2563eb' }, // Blue
    { color: '#16a34a' }, // Green
    { color: '#ca8a04' }, // Yellow
    { color: '#f8fafc', stripeColor: '#64748b' }, // White with gray stripes
    { color: '#1e293b' }, // Dark slate
    { color: '#ea580c' }, // Orange
    { color: '#9333ea' }, // Purple
  ];
  
  const index = Math.abs(hash) % colors.length;
  if (!isHome) {
    const awayIndex = (index + 3) % colors.length;
    return colors[awayIndex];
  }
  return colors[index];
}

function formatOdds(odds: string | number): string {
  const parsed = parseFloat(String(odds));
  return isNaN(parsed) || parsed < 1.01 ? '-' : parsed.toFixed(2).replace('.', ',');
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

interface MobileTopEventsTableSectionProps {
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
}

export const MobileTopEventsTableSection: React.FC<MobileTopEventsTableSectionProps> = ({
  bets,
  onAddBet,
  onRemoveBet,
}) => {
  const { locale } = useTranslation();
  const navigate = useNavigate();
  const [preEvents, setPreEvents] = useState<EventSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState('Soccer');
  const [activeBetFilter, setActiveBetFilter] = useState<string | number>('Tipp');

  const handleEventClick = (ev: EventSnapshot) => {
    navigate('/match/' + ev.event_id, { state: { event: ev } });
  };

  const loadData = useCallback(async (options?: RequestInit) => {
    try {
      const topRes = await getTopEvents({ limit: 10 }, options);
      if (topRes && topRes.items) {
        const eventsData = await Promise.all(
          topRes.items.map(async (item) => {
            try {
              return await getEvent(item.event_id);
            } catch (err) {
              return null;
            }
          })
        );
        const validEvents = eventsData.filter((e) => e !== null) as EventSnapshot[];
        setPreEvents(validEvents);
      }
    } catch (e) {
      if ((e as any)?.name !== 'AbortError') {
        console.error('Failed to load top events for MobileTopEventsTableSection', e);
      }
    } finally {
      if (!options?.signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    loadData({ signal: abortController.signal });

    return () => {
      abortController.abort();
    };
  }, [loadData]);

  // Extract unique sports
  const sportCounts: Record<string, number> = {};
  const sportNames: Record<string, string> = {};
  for (const ev of preEvents) {
    const sId = ev.event.sport_id;
    const sName = ev.event.sport_name;
    if (sId && sName) {
      sportCounts[sId] = (sportCounts[sId] || 0) + 1;
      sportNames[sId] = sName;
    }
  }

  const SPORT_ORDER_BY_ID: Record<string, number> = {
    'sr:sport:1': 1, // Soccer
    'sr:sport:2': 2, // Basketball
    'sr:sport:5': 3, // Tennis
    'sr:sport:4': 4, // Ice Hockey
    'sr:sport:23': 5, // Volleyball
    'sr:sport:6': 6, // Handball
    'sr:sport:20': 7 // Table Tennis
  };

  const sportTabs = Object.entries(sportCounts)
    .filter(([id]) => id in SPORT_ORDER_BY_ID)
    .sort((a, b) => (SPORT_ORDER_BY_ID[a[0]] ?? 999) - (SPORT_ORDER_BY_ID[b[0]] ?? 999))
    .slice(0, 5)
    .map(([id, count]) => ({ name: sportNames[id], count, emoji: SPORT_EMOJI[sportNames[id]] || '🏅' }));

  // Fallback active sport
  useEffect(() => {
    if (sportTabs.length > 0 && !sportTabs.some(t => t.name === activeSport)) {
      if (sportTabs.some(t => t.name === 'Soccer')) {
        setActiveSport('Soccer');
      } else {
        setActiveSport(sportTabs[0].name);
      }
    }
  }, [sportTabs, activeSport]);

  const filteredEvents = preEvents.filter((ev) => ev.event.sport_name === activeSport);
  const sportFilters = getMarketOptionsForSport(activeSport, false);
  const availableFilters = sportFilters.filter((f) => filteredEvents.some((ev) => isFilterAvailable(f, ev.markets)));
  const displayedEvents = filteredEvents.filter((ev) => isFilterAvailable(activeBetFilter, ev.markets)).slice(0, 8);

  useEffect(() => {
    const available = sportFilters.filter((f) => filteredEvents.some((ev) => isFilterAvailable(f, ev.markets)));
    if (available.length > 0) {
      if (!available.includes(activeBetFilter)) {
        setActiveBetFilter(available[0]);
      }
    } else if (sportFilters.length > 0 && !sportFilters.includes(activeBetFilter)) {
      setActiveBetFilter(sportFilters[0]);
    }
  }, [filteredEvents, activeBetFilter, activeSport, sportFilters]);

  const isSelected = (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string>) =>
    bets.some((b) => b.id === generateBetItemId(eventId, marketId, outcomeId, specifiers));

  const handleOddsClick = (ev: EventSnapshot, market: Market, outcome: Outcome, tippLabel: string) => {
    const id = generateBetItemId(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);
    if (isSelected(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers)) {
      onRemoveBet(id);
    } else {

      const formattedTipp = formatTippLabelWithAttr(tippLabel, market);
      onAddBet({
        id,
        matchId: 0,
        matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
        betType: tippLabel === 'Heimsieg' ? 'home' : tippLabel === 'Unentschieden' ? 'draw' : 'away',
        tippLabel: formattedTipp,
        odds: formatOdds(outcome.odds),
        eventId: ev.event_id,
        marketId: market.market_id,
        outcomeId: outcome.outcome_id,
        outcomeName: (outcome as any).player_name || (outcome as any).playerName || outcome.name,
        specifiers: market.specifiers
          ? typeof market.specifiers === 'string'
            ? market.specifiers
            : Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
          : '',
      });
    }
  };

  if (loading) {
    return (
      <section className="px-3 py-2">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-base font-black text-gray-900">Top Eventss</h2>
        </div>
        <div className="flex justify-center items-center h-24 bg-white rounded-2xl shadow-sm">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
        </div>
      </section>
    );
  }

  if (preEvents.length === 0) {
    return null;
  }

  const renderOddsButtons = (ev: EventSnapshot, items: { label: string; outcome: Outcome; market: Market; tipp: string }[]) => (
    <div className="flex gap-2 flex-1">
      {items.map(({ label, outcome, market, tipp }) => {
        const sel = isSelected(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);
        const oddsVal = formatOdds(outcome.odds);
        const disabled = !outcome.active || oddsVal === '-' || parseFloat(String(outcome.odds)) < 1.01;
        return (
          <OddsButton
            odds={outcome.odds}
            selected={sel}
            key={label}
            onClick={() => handleOddsClick(ev, market, outcome, tipp)}
            disabled={disabled}
            className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 w-full border ${sel ? 'bg-[#333] border-[#333]' : disabled ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
          >
            <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>
              {formatOddsButtonLabel(label, ev.event.home.name, ev.event.away.name, locale)}
            </span>
            <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>
              {disabled ? <LockIcon /> : oddsVal}
            </span>
          </OddsButton>
        );
      })}
    </div>
  );

  return (
    <section className="px-3 py-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-black text-gray-900">Top Eventss</h2>
        <button onClick={() => navigate('/todays-matches')} className="flex items-center gap-1 text-sm font-semibold text-gray-700">
          Alle
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {sportTabs.length > 0 && (
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex gap-4 overflow-x-auto scrollbar-hide mb-3">
          {sportTabs.map((sport) => {
            const active = activeSport === sport.name;
            return (
              <button
                key={sport.name}
                onClick={() => setActiveSport(sport.name)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition whitespace-nowrap text-xs font-semibold ${
                  active ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-gray-50 text-gray-600 border border-transparent'
                }`}
              >
                <span>{sport.emoji}</span>
                <span>{sport.name === 'Soccer' ? 'Fußball' : sport.name === 'eSoccer' ? 'eFootball' : sport.name}</span>
                <span className={`text-[10px] px-1 rounded-full ${active ? 'bg-red-200 text-red-700' : 'bg-gray-200 text-gray-600'}`}>{sport.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {availableFilters.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {availableFilters.map((f) => (
            <button
              key={f}
              onClick={() => setActiveBetFilter(f)}
              className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition ${
                activeBetFilter === f
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              {translateMarketFilterLabel(f, locale)}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 mt-2">
        {displayedEvents.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">
            {filteredEvents.length === 0 ? "Bu spor için maç yok." : "Bu market için maç yok."}
          </div>
        ) : (
          displayedEvents.map((ev, idx) => {
            const matchTime = formatTime(ev.event.scheduled_at);
            const team1 = ev.event.home.name;
            const team2 = ev.event.away.name;
            const homeJersey = getJerseyColors(team1, true);
            const awayJersey = getJerseyColors(team2, false);

            return (
              <div key={ev.event_id}>
                {idx > 0 && <div className="border-t border-gray-100 mx-4" />}
                <div className="px-4 py-3">
                  <div className="flex mb-3 cursor-pointer" onClick={() => handleEventClick(ev)}>
                    <div className="text-left min-w-[45px] flex-shrink-0 pt-0.5 flex flex-col justify-start">
                      <span className="text-xs font-bold text-gray-900 leading-none">{matchTime}</span>
                      <span className="text-[10px] text-gray-400 font-semibold mt-1">Bugün</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-0.5">
                        <div className="flex items-center gap-2 truncate">
                          <img src={ev.event.home.crest_url || '/default-crest.svg'} alt="" className="w-5 h-5 object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                          <span className="text-sm font-semibold text-gray-900 truncate">{ev.event.home.name}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 truncate">
                          <img src={ev.event.away.crest_url || '/default-crest.svg'} alt="" className="w-5 h-5 object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).src = '/default-crest.svg'; }} />
                          <span className="text-sm font-semibold text-gray-900 truncate">{ev.event.away.name}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <div className="flex gap-2 items-center">
                      {(() => {
                        const layoutGrp = getLayoutGroupForMarketId(activeBetFilter);
                        let attrMarket: Market | undefined;
                        if (layoutGrp === 'Tipp') { const r = find1x2Market(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Tipp Restzeit') { const r = findRestzeitGeneric(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Über/Unter') { const r = findOUGeneric(ev.markets, false, ev); attrMarket = r?.market; }
                        else if (layoutGrp === 'Nächstes Tor') { const r = findNextGoal(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Handicap' || layoutGrp === 'Satz-Handicap') { const r = findHandicap(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Beide Teams treffen') { const r = findBTTSGeneric(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Halbzeit-Über/Unter') { const r = findOUGeneric(ev.markets, true, ev); attrMarket = r?.market; }
                        else if (layoutGrp === 'Doppelte Chance') { const r = findDCGeneric(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === 'Head-To-Head' || layoutGrp === 'Sieger' || layoutGrp === 'Draw No Bet') { const r = findH2H(ev.markets); attrMarket = r?.market; }
                        else if (layoutGrp === '1. Halbzeit') { const r = find1stHalfMarket(ev.markets); attrMarket = r?.market; }
                        if (!attrMarket) return null;
                        const attr = getMarketAttr(attrMarket);
                        if (!attr) return null;
                        return <span className="text-[10px] font-bold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{attr}</span>;
                      })()}

                      {(() => {
                        const layoutGrp = getLayoutGroupForMarketId(activeBetFilter);
                        if (layoutGrp === 'Tipp') {
                          const m = find1x2Market(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Heimsieg' },
                            { label: 'X', outcome: m.draw, market: m.market, tipp: 'Unentschieden' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Auswärtssieg' },
                          ]);
                        }
                        if (layoutGrp === 'Tipp Restzeit') {
                          const m = findRestzeitGeneric(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Restzeit Heimsieg' },
                            { label: 'X', outcome: m.draw, market: m.market, tipp: 'Restzeit Unentschieden' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Restzeit Auswärtssieg' },
                          ]);
                        }
                        if (layoutGrp === 'Über/Unter') {
                          const m = findOUGeneric(ev.markets, false, ev);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: 'Über', outcome: m.over, market: m.market, tipp: 'Über' },
                            { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Unter' },
                          ]);
                        }
                        if (layoutGrp === 'Nächstes Tor') {
                          const m = findNextGoal(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Nächstes Tor Heim' },
                            { label: 'Kein Tor', outcome: m.none, market: m.market, tipp: 'Kein Tor' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Nächstes Tor Auswärts' },
                          ]);
                        }
                        if (layoutGrp === 'Handicap' || layoutGrp === 'Satz-Handicap') {
                          const m = findHandicap(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          const buttons = [
                            { label: '1', outcome: m.o1, market: m.market, tipp: 'Handicap Heim' },
                          ];
                          if (m.o3) {
                            buttons.push({ label: 'X', outcome: m.o3, market: m.market, tipp: 'Handicap Unentschieden' });
                          }
                          buttons.push({ label: '2', outcome: m.o2, market: m.market, tipp: 'Handicap Auswärts' });
                          return renderOddsButtons(ev, buttons);
                        }
                        if (layoutGrp === 'Beide Teams treffen') {
                          const m = findBTTSGeneric(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: 'Ja', outcome: m.yes, market: m.market, tipp: 'Ja' },
                            { label: 'Nein', outcome: m.no, market: m.market, tipp: 'Nein' },
                          ]);
                        }
                        if (layoutGrp === 'Halbzeit-Über/Unter') {
                          const m = findOUGeneric(ev.markets, true, ev);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: 'Über', outcome: m.over, market: m.market, tipp: '1. Halbzeit Über' },
                            { label: 'Unter', outcome: m.under, market: m.market, tipp: '1. Halbzeit Unter' },
                          ]);
                        }
                        if (layoutGrp === 'Doppelte Chance') {
                          const m = findDCGeneric(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1X', outcome: m.o1x, market: m.market, tipp: '1X' },
                            { label: '12', outcome: m.o12, market: m.market, tipp: '12' },
                            { label: 'X2', outcome: m.ox2, market: m.market, tipp: 'X2' },
                          ]);
                        }
                        if (layoutGrp === 'Head-To-Head' || layoutGrp === 'Sieger' || layoutGrp === 'Draw No Bet') {
                          const m = findH2H(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: 'Sieger Heim' },
                            { label: '2', outcome: m.away, market: m.market, tipp: 'Sieger Auswärts' },
                          ]);
                        }
                        if (layoutGrp === '1. Halbzeit') {
                          const m = find1stHalfMarket(ev.markets);
                          if (!m) return <div className="flex gap-2 flex-1">{[1,2,3].map((i) => <div key={i} className="flex-1 h-[36px] flex items-center justify-center rounded-lg bg-gray-50 opacity-40"><LockIcon /></div>)}</div>;
                          return renderOddsButtons(ev, [
                            { label: '1', outcome: m.home, market: m.market, tipp: '1. Halbzeit Heim' },
                            { label: 'X', outcome: m.draw, market: m.market, tipp: '1. Halbzeit Unentschieden' },
                            { label: '2', outcome: m.away, market: m.market, tipp: '1. Halbzeit Auswärts' },
                          ]);
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
};
